import { MessageView } from './message-model';

export type AuthorLike = Pick<MessageView, 'authorRole' | 'authorUserSub' | 'authorName'>;

/**
 * Display label for a message author. Identity rules:
 *  - Your own messages read as "Me" (matched by sub, never the raw name).
 *  - Aktify-side (admin) authors show their real name ONLY to internal viewers; to a
 *    client viewer they stay the opaque "Aktify Team" — clients don't need to know which
 *    Aktify person answered.
 *  - Client authors show their real name to everyone. Comments/chat are company-scoped
 *    server-side, so a client only ever sees fellow same-company client names.
 * Falls back to the previous role-based placeholders when no name is available (e.g. the
 * gateway couldn't enrich it, or an older row created before name enrichment).
 */
export const authorLabel = (
  msg: AuthorLike,
  currentUserSub: string,
  viewerIsInternal: boolean,
): string => {
  if (msg.authorUserSub === currentUserSub) return 'Me';
  if (msg.authorRole === 'admin') {
    return (viewerIsInternal && msg.authorName) || 'Aktify Team';
  }
  return msg.authorName || 'User';
};

/**
 * Avatar initials derived from a display label, so the avatar always matches the
 * name shown next to it (and inherits the same viewer-role gating). Takes the first
 * letter of the first two words: "Ada Lovelace" → "AL", single word → its first
 * letter ("User" → "U", "Me" → "M").
 */
export const initialsFrom = (label: string): string =>
  label
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
